import {organizationMemberDetailsApi} from '@configuration';
import {defineStore, storeToRefs} from 'pinia';
import {ref, useAuth, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {OrganizationRole, OrganizationRoleUpdateRequest} from '@api';

export const useOrganizationMemberStore = defineStore('organization-member-store', () => {
    const {user} = storeToRefs(useAuth());
    const {t} = useTranslation();
    const loggedInUserOrganisationRole = ref<OrganizationRole | undefined>(undefined);
    const selectedOrganizationId = ref<string | undefined>();

    async function updateOrganizationRole(organizationId: string, accountId: string, role: OrganizationRole): Promise<void> {
        try {
            const request: OrganizationRoleUpdateRequest = {role};
            await organizationMemberDetailsApi.setRole({
                organizationId,
                accountId,
                organizationRoleUpdateRequest: request,
            });
            pushVJoySnackbar({props: {message: t('general.form.success'), level: 'success'}});
        } catch {
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
        }
    }

    async function initOrganizationRole(organizationId: string, accountId: string): Promise<void> {
        // Malt admins have no role on organizations
        if (user.value?.platformAdmin) {
            return;
        }

        try {
            loggedInUserOrganisationRole.value = await organizationMemberDetailsApi.getRole({
                organizationId,
                accountId,
            });
        } catch (e) {
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
            return Promise.reject(e);
        }
    }

    function getOrganizationRoleDisplayName(role: string): string {
        return t(`client.team.members.list.organizationRole.${(role as OrganizationRole) ?? 'MEMBER'}`);
    }

    return {
        updateOrganizationRole,
        initOrganizationRole,
        getOrganizationRoleDisplayName,
        loggedInUserOrganisationRole: readonlyState(loggedInUserOrganisationRole),
        selectedOrganizationId,
    };
});
